import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { Port } from './useData';

export const moduleConfig = {
  module: {
    title: 'Ports',
    permissionModuleKey: 'ports',
    route: {
      list: '/parameters/ports',
      new: '/parameters/ports/new',
      detail: (id: number) => `/parameters/ports/${id}`,
      edit: (id: number) => `/parameters/ports/${id}/edit`},
    api: {
      list: '/ports',
      detail: (id: number) => `/ports/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by port name or code...' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['name', 'isActive']},
  ],
  filters: {
    fields: [
      {
        key: 'name',
        label: 'Name',
        placeholder: 'All names',
        getValue: (item: Port) => item.name,
        options: []},
      {
        key: 'isActive',
        label: 'Active',
        type: 'boolean' as const,
        getValue: (item: Port) => (item.isActive ? 'Yes' : 'No'),
        options: [
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
        ]},
    ],
    applyFilter: (items: Port[], filters: Record<string, string[]>) => {
      return items.filter((item) => {
        if (filters.name?.length) {
          const v = item.name || '';
          if (!filters.name.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }
        if (filters.isActive?.length) {
          const s = item.isActive ? 'Yes' : 'No';
          if (!filters.isActive.includes(s)) return false;
        }
        return true;
      });
    }},
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New port',
        showTitle: false,
        variant: 'primary' as const,
        href: '/parameters/ports/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: Port) => item.id,
  },
  emptyStates: {
    noData: 'Create a port to get started.',
    noSearchResults: 'No ports match your search.',
    noFilterResults: 'No ports match the filters.',
  },
  delete: {
    title: 'Delete port',
    message: 'Delete this port?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete port.',
    handler: async (id: number) => {
      const { deletePort } = await import('../../../services/mutations');
      await deletePort(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This port does not exist.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Port',
    getTitle: (item: Port) => item.name || `Port #${item.id}`},
  form: {
    new: { title: 'New port', subtitle: 'Create a port record', submitText: 'Create' },
    edit: { title: 'Edit port', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<Port>) => {
      const { createPort } = await import('../../../services/mutations');
      return createPort(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<Port>) => {
      const { updatePort } = await import('../../../services/mutations');
      return updatePort(id, data as Record<string, unknown>);
    }},
  fields: [
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Port name',
      validation: { required: true, minLength: 1, maxLength: 120 }},
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      placeholder: 'Port code',
      validation: { maxLength: 20 }},
    {
      name: 'countryId',
      label: 'Country',
      type: 'select',
      placeholder: 'Select country',
      apiEndpoint: '/countries',
      valueField: 'id',
      labelField: 'name',
      clearable: true},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'main',
        title: 'Port',
        icon: '',
        gridCols: 'md:grid-cols-12',
        fields: ['name', 'code', 'countryId', 'isActive'],
        fieldSpans: {
          name: 'md:col-span-6',
          code: 'md:col-span-6',
          countryId: 'md:col-span-12',
          isActive: 'md:col-span-12'},
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (
  formatDate: (dateString?: string) => string,
  options?: { hideCountry?: boolean },
): Column<Port>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v} /> },
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  ...(options?.hideCountry
    ? []
    : [
        {
          key: 'country.name',
          header: 'Country',
          sortable: true,
          render: (_v: unknown, row?: Port) => <TextCell value={row?.country?.name} />} as Column<Port>,
      ]),
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v, row) => (
      <Badge variant={v ?? row?.isActive ? 'success' : 'danger'} className="shrink-0 border rounded-md px-2 py-0.5">
        {v ?? row?.isActive ? 'Yes' : 'No'}
      </Badge>
    )},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

export const getDetailSections = (item: Port, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Name', value: item.name },
      { label: 'Code', value: item.code || '' },
      { label: 'Country', value: item.country?.name || '' },
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
    ]},
];

const TableActionsCell: React.FC<{
  item: Port;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => (
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    )}
    {canDelete && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    )}
  </div>
);

export const getTableActions = (
  item: Port,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};

export const getStatusBadges = () => null;

export const getInitialFormData = (): Partial<Port> => ({
  name: '',
  code: '',
  countryId: null,
  isActive: true});

export const mapItemToFormData = (item: Port): Partial<Port> => ({
  name: item.name,
  code: item.code ?? '',
  countryId: item.countryId ?? null,
  isActive: item.isActive ?? true});

export type ModuleConfig = typeof moduleConfig;
